import { FC, useEffect, useMemo, useRef } from "react";
import { format } from "date-fns";
import classNames from "classnames";
import { Avatar } from "../shared";
import { Link } from "react-router-dom";

interface TimelineItem {
  description: string;
  timestamp: string;
  type?: "message" | "note" | "status" | "update" | "approval";
  sender?: {
    name: string;
    avatar?: string;
  };
  isOutbound?: boolean;
  metadata?: Record<string, unknown>;
  isFullWidth?: boolean;
  readReceiptLabel?: string;
}

interface CommunicationsTimelineProps {
  items?: TimelineItem[];
  loading?: boolean;
}

function buildReferenceTo(type: string, id: string) {
  switch (type) {
    case "job":
      return `/jobs/${id}`;
    case "manifest":
      return `/manifests/${id}`;
    case "invoice":
      return `/invoices/${id}`;
    case "payout":
      return `/payouts/${id}`;
    case "equipment":
      return `/equipment/${id}`;
    case "client":
      return `/clients/${id}`;
    case "user":
      return `/users/${id}`;
    case "leg": {
      // id is encoded as "{jobId}:{legId}"
      const [jobId, legId] = String(id || "").split(":");
      if (jobId && legId) return `/jobs/${jobId}#leg-${legId}`;
      return "/";
    }
    default:
      return "/";
  }
}

/**
 * Render message content with reference tokens as links.
 *
 * Token format (stored in message text): `@[type:id|label]`
 * Example: `@[job:64f...|JOB-123]`
 */
function renderMessageContent(text: string) {
  const value = String(text || "");
  const out: any[] = [];
  const re = /@\[(?<type>[a-zA-Z]+):(?<id>[^\]|]+)\|(?<label>[^\]]+)\]/g;

  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = re.exec(value))) {
    const start = match.index;
    const end = re.lastIndex;
    if (start > last) out.push(value.slice(last, start));

    const type = (match.groups?.type || "").toLowerCase();
    const id = match.groups?.id || "";
    const label = match.groups?.label || `${type}:${id}`;
    const to = buildReferenceTo(type, id);

    out.push(
      <Link
        key={`${type}:${id}:${start}`}
        to={to}
        className="inline-flex items-center px-1.5 py-[1px] rounded-md bg-primary-100/60 text-primary-800 hover:bg-primary-100 text-[11px] leading-tight"
      >
        {label}
      </Link>
    );

    last = end;
  }
  if (last < value.length) out.push(value.slice(last));
  return out;
}

const Message: FC<TimelineItem> = ({
  description,
  timestamp,
  type,
  metadata,
  sender,
}) => {
  return (
    <div className="flex flex-col items-center my-3 w-full">
      <div className="bg-gray-50 rounded-lg px-4 py-2 max-w-[100%] w-full">
        <div className="text-xs text-gray-400 text-center mt-1">
          {format(new Date(timestamp), "h:mm a")}
        </div>
        <div className="flex items-center justify-center gap-2">
          {type === "status" && (
            <div className="w-2 h-2 rounded-full bg-green-500" />
          )}
          {type === "update" && (
            <div className="w-2 h-2 rounded-full bg-blue-500" />
          )}
          {type === "note" && (
            <div className="w-2 h-2 rounded-full bg-yellow-500" />
          )}
          {type === "approval" &&
            (metadata as any)?.status?.content === "approved" && (
              <div className="w-2 h-2 rounded-full bg-green-500" />
            )}
          {type === "approval" &&
            (metadata as any)?.status?.content === "rejected" && (
              <div className="w-2 h-2 rounded-full bg-red-500" />
            )}

          <span className="text-sm text-gray-600 text-center">
            {description}
          </span>
        </div>
        {metadata && (
          <div className="w-full">
            {Object.entries(metadata as any)
              .filter(([key, value]) => !(value as any).hidden)
              .map(([key, value]) => {
                const metadataValue = value as any;
                return (
                  metadataValue.content &&
                  (metadataValue?.type === "message" ? (
                    <MessageBubble
                      description={metadataValue.content}
                      timestamp={null}
                      type={metadataValue.type}
                      isOutbound={metadataValue.isOutbound}
                      sender={sender}
                    />
                  ) : (
                    <div key={key} className="text-xs text-gray-500">
                      {key}: {value as any}
                    </div>
                  ))
                );
              })}
          </div>
        )}
      </div>
    </div>
  );
};

const MessageBubble: FC<TimelineItem> = ({
  description,
  timestamp,
  isOutbound,
  sender,
  isFullWidth,
  readReceiptLabel,
}) => {
  const showAvatar = Boolean(sender?.name || sender?.avatar);

  const bubbleClass = classNames(
    // Bubble styling (kept fully rounded)
    "rounded-2xl px-3 py-2 shadow-sm",
    // Use width much closer to mobile: allow bubbles to expand almost fully
    "w-fit max-w-full",
    // Readability + wrapping
    "text-sm leading-snug break-words whitespace-pre-wrap",
    {
      "w-full": isFullWidth,
      // Fully-rounded bubbles (no corner notch)
      "bg-primary-600 text-white ml-auto": isOutbound,
      "bg-gray-100 text-gray-900": !isOutbound,
    }
  );

  const timeClass = classNames("text-[10px] mt-1", {
    "text-gray-400 text-right": isOutbound,
    "text-gray-400": !isOutbound,
  });

  return (
    <div className={classNames("flex w-full", { "justify-end": isOutbound })}>
      <div
        className={classNames("flex w-full gap-2", {
          "flex-row-reverse": isOutbound,
          "items-end": true,
        })}
      >
        {showAvatar && (
          <div className="shrink-0 self-end pb-1">
            <Avatar
              size="xs"
              src={sender?.avatar}
              name={sender?.name || "User"}
              className="bg-gray-400"
            />
          </div>
        )}

        <div
          className={classNames("flex flex-col min-w-0 flex-1", {
            "items-end": isOutbound,
          })}
        >
          {!isOutbound && sender?.name && (
            <span className="text-[11px] text-gray-500 mb-1 ml-1">
              {sender.name}
            </span>
          )}

          <div className={bubbleClass}>
            <div>{renderMessageContent(description)}</div>
          </div>

          {(timestamp || readReceiptLabel) && (
            <div
              className={classNames(timeClass, "flex items-center gap-2", {
                "mr-1 justify-end": isOutbound,
                "ml-1": !isOutbound,
              })}
            >
              {timestamp && <span>{format(new Date(timestamp), "h:mm a")}</span>}
              {isOutbound && readReceiptLabel && (
                <span className="text-[10px] font-medium text-gray-500">
                  {readReceiptLabel}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const DateSeparator: FC<{ date: string }> = ({ date }) => (
  <div className="flex justify-center my-4">
    <div className="bg-gray-200 rounded-full px-4 py-1 text-xs text-gray-600">
      {format(new Date(date), "MMMM d, yyyy")}
    </div>
  </div>
);

const CommunicationsTimeline: FC<CommunicationsTimelineProps> = ({
  items = [],
  loading = false,
}) => {
  const timelineRef = useRef<HTMLDivElement | null>(null);
  const validItems = useMemo(() => {
    return items.filter((item) => {
      const timestamp = new Date(item.timestamp);
      return item.timestamp && !isNaN(timestamp.getTime());
    });
  }, [items, items?.length]);
  const lastItemTimestamp =
    validItems.length > 0 ? validItems[validItems.length - 1]?.timestamp : null;

  useEffect(() => {
    if (loading || !timelineRef.current) return;

    const scrollContainer = timelineRef.current.parentElement;
    if (!scrollContainer) return;

    requestAnimationFrame(() => {
      scrollContainer.scrollTop = scrollContainer.scrollHeight;
    });
  }, [loading, validItems.length, lastItemTimestamp]);

  // Show loading state if explicitly loading
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        <div className="animate-pulse">Loading timeline...</div>
      </div>
    );
  }

  // Show empty state only if we have no valid items and aren't loading
  if (!validItems.length) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        No messages yet
      </div>
    );
  }

  let currentDate = "";

  return (
    <div ref={timelineRef} className="p-2 space-y-2">
      {validItems.map((item, index) => {
        try {
          const messageDate = format(new Date(item.timestamp), "yyyy-MM-dd");
          const showDateSeparator = messageDate !== currentDate;
          currentDate = messageDate;

          return (
            <div key={index}>
              {showDateSeparator && <DateSeparator date={item.timestamp} />}
              {item.type === "message" ? (
                <MessageBubble {...item} />
              ) : (
                <Message {...item} />
              )}
            </div>
          );
        } catch (error) {
          console.error("Error rendering timeline item:", error, item);
          return null;
        }
      })}
    </div>
  );
};

export default CommunicationsTimeline;
